import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { useScrimDismiss } from '../components/useScrimDismiss';
import { useI18n } from '../i18n';
import { useAppConfig } from '../app-config';
import { interpolate } from '../gallery/format';
import {
  ZOOM_STEP,
  anchorOffset,
  clamp,
  clampOffset,
  computeZoomBounds,
  isPannable,
  pinchOffset,
} from './zoom';
import { useSwipeNav } from './use-swipe-nav';

/**
 * Image zoomable/panoramable de la visionneuse.
 *
 * - Au repos, l'image remplit 100 % de la frame (fit « contain » : elle touche le
 *   bord largeur OU hauteur) ; une image PLUS PETITE que cette boîte n'est agrandie
 *   que jusqu'à un plafond CONFIGURABLE (réglage global superadmin, défaut +50 % ;
 *   cf. [[zoom]]) — pas de gros upscaling flou. Charge l'ORIGINAL (netteté au zoom).
 * - Zoom : molette (ancrée au curseur), boutons +/−, clavier `+`/`−`/`0`,
 *   double-clic (ajuster ↔ agrandir), et **PINCER à deux doigts** (mobile —
 *   ancré au milieu des doigts, + pan à deux doigts). Panoramique par glisser
 *   quand l'image déborde. Bornes et décalages calculés par le module pur `zoom.ts`.
 * - Dégrade proprement là où le conteneur n'est pas mesurable (pas de
 *   `ResizeObserver`, ex. happy-dom) : rendu `object-contain` neutre, sans zoom.
 */
/** Déplacement (px) au-delà duquel un geste est un GLISSER, pas un clic. */
const CLICK_DRAG_THRESHOLD = 5;

interface ZoomableImageProps {
  src: string;
  alt: string;
  /** Dimensions natives connues d'avance (évite le saut de layout) ; sinon lues au chargement. */
  naturalWidth: number | null;
  naturalHeight: number | null;
  onError?: () => void;
  /**
   * Clic (pas un glisser) sur le FOND, en dehors des pixels de l'image → retour
   * galerie. Ignoré au clic sur l'image, sur un contrôle, ou en fin de pan.
   */
  onBackdropClick?: () => void;
  /**
   * Navigation par balayage tactile (mobile). Active UNIQUEMENT quand l'image
   * n'est pas zoomée (au zoom, le glisser sert au panoramique). Bornée par
   * `canPrev`/`canNext`.
   */
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
  /**
   * ROTATION OPTIMISTE de l'affichage, en degrés (multiple de 90, signé). Sert à la
   * visionneuse : « Pivoter » enfile un JOB asynchrone, et sans retour immédiat
   * l'utilisateur reclique en croyant que rien ne s'est passé. On fait donc tourner
   * les pixels À L'ÉCRAN tout de suite, en attendant que l'original réécrit arrive.
   *
   * À ±90° (mod 180), la rotation ÉCHANGE la largeur et la hauteur OCCUPÉES : une photo
   * 4000×3000 pivotée tient dans une boîte 3000×4000. Le cadrage (`computeZoomBounds`)
   * et le bornage du panoramique raisonnent donc sur les dimensions ÉCHANGÉES, sans quoi
   * l'image pivotée déborderait du cadre (ou y flotterait, trop petite).
   *
   * OPTIONNELLE et nulle par défaut : le Tagueur (`TagWalk`) et la page publique
   * (`PublicShareViewer`) ne la passent pas et gardent exactement leur comportement.
   */
  rotationDeg?: number;
}

interface Size {
  w: number;
  h: number;
}

export function ZoomableImage({
  src,
  alt,
  naturalWidth,
  naturalHeight,
  onError,
  onBackdropClick,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  rotationDeg = 0,
}: ZoomableImageProps): React.JSX.Element {
  const { t } = useI18n();
  // Plafond d'agrandissement des petites images = réglage global (superadmin, console
  // « Général »), en RATIO (150 % → 1,5). Défaut prudent hors provider (cf. useAppConfig).
  const { smallImageUpscalePercent } = useAppConfig();
  const maxInitialUpscale = smallImageUpscalePercent / 100;
  const containerRef = useRef<HTMLDivElement>(null);

  const [size, setSize] = useState<Size>({ w: 0, h: 0 });
  const [natural, setNatural] = useState<Size>({ w: naturalWidth ?? 0, h: naturalHeight ?? 0 });
  const [scale, setScale] = useState<number | null>(null);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [interacted, setInteracted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Mesure du conteneur (ResizeObserver ; repli mono-mesure si absent — happy-dom).
  // `useLayoutEffect` : mesure AVANT peinture → cadrage correct au 1er rendu (pas de saut).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = (): void => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Dimensions natives telles qu'elles OCCUPENT le cadre : un quart de tour (±90° mod
  // 180) échange largeur et hauteur. Tout ce qui borne (cadrage, débordement,
  // panoramique) raisonne sur ces dimensions-là ; seule la BOÎTE CSS de l'`<img>`
  // garde les dimensions non pivotées, puisque `rotate()` la fait tourner ensuite.
  const quarterTurn = ((rotationDeg % 180) + 180) % 180 === 90;
  const framedW = quarterTurn ? natural.h : natural.w;
  const framedH = quarterTurn ? natural.w : natural.h;

  const bounds = computeZoomBounds(framedW, framedH, size.w, size.h, maxInitialUpscale);
  const effScale = scale ?? bounds?.initial ?? 1;
  const dispW = framedW * effScale;
  const dispH = framedH * effScale;
  const clampedX = clampOffset(offset.x, dispW, size.w);
  const clampedY = clampOffset(offset.y, dispH, size.h);
  const ready = bounds !== null;
  const pannable = ready && isPannable(dispW, dispH, size.w, size.h);
  const percent = Math.round(effScale * 100);

  // Valeurs « vivantes » pour les écouteurs stables (molette/clavier/pan). `framed` =
  // dimensions natives APRÈS rotation (cf. `framedW`/`framedH`) : c'est la boîte que
  // l'image occupe réellement, donc la seule contre laquelle borner les décalages.
  const framed: Size = { w: framedW, h: framedH };
  const live = useRef({ scale: effScale, offset, bounds, framed, size, pannable });
  live.current = { scale: effScale, offset, bounds, framed, size, pannable };

  // Vue par défaut : cale sur `initial` tant que l'utilisateur n'a pas interagi ;
  // sinon re-borne l'échelle (redimensionnement de la fenêtre). Dépendances
  // PRIMITIVES (`bounds` est recréé à chaque rendu).
  const bInitial = bounds?.initial ?? null;
  const bMin = bounds?.min ?? null;
  const bMax = bounds?.max ?? null;
  useEffect(() => {
    if (bInitial === null || bMin === null || bMax === null) return;
    setScale((prev) => (prev === null || !interacted ? bInitial : clamp(prev, bMin, bMax)));
    if (!interacted) setOffset({ x: 0, y: 0 });
  }, [bInitial, bMin, bMax, interacted]);

  /** Applique une échelle cible en gardant le point (cx,cy) — relatif au centre — sous le curseur. */
  const applyZoom = useCallback((target: number, cx: number, cy: number) => {
    const { bounds: b, framed: nat, size: sz, scale: cur, offset: off } = live.current;
    if (!b) return;
    const ns = clamp(target, b.min, b.max);
    if (ns === cur) return;
    const k = ns / cur;
    const nx = clampOffset(anchorOffset(off.x, cx, k), nat.w * ns, sz.w);
    const ny = clampOffset(anchorOffset(off.y, cy, k), nat.h * ns, sz.h);
    setInteracted(true);
    setScale(ns);
    setOffset({ x: nx, y: ny });
  }, []);

  const zoomInCenter = useCallback(
    () => applyZoom(live.current.scale * ZOOM_STEP, 0, 0),
    [applyZoom],
  );
  const zoomOutCenter = useCallback(
    () => applyZoom(live.current.scale / ZOOM_STEP, 0, 0),
    [applyZoom],
  );
  const reset = useCallback(() => {
    setInteracted(false);
    setOffset({ x: 0, y: 0 });
    if (live.current.bounds) setScale(live.current.bounds.initial);
  }, []);

  // Molette = zoom ancré au curseur (listener natif non-passif pour `preventDefault`).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent): void => {
      if (!live.current.bounds) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - (rect.left + rect.width / 2);
      const cy = e.clientY - (rect.top + rect.height / 2);
      applyZoom(live.current.scale * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP), cx, cy);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [applyZoom]);

  // Clavier : `+`/`−`/`0` (ignoré si un champ de saisie a le focus — ex. notes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable))
        return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomInCenter();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOutCenter();
      } else if (e.key === '0') {
        e.preventDefault();
        reset();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomInCenter, zoomOutCenter, reset]);

  // --- Gestes pointeur : pan 1 doigt, PINCER 2 doigts (zoom + pan) --------------

  // Pointeurs tactiles actifs (id → position écran). Distingue 1 doigt (swipe/pan)
  // de 2 doigts (pincer). Vidé implicitement à chaque média (composant keyé par id).
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  // Instantané pris à l'ENTRÉE du pincer : distance entre doigts, milieu (relatif au
  // centre), échelle et décalage de départ. `null` = aucun pincer en cours.
  const pinchRef = useRef<{
    dist: number;
    scale: number;
    offset: { x: number; y: number };
    midX: number;
    midY: number;
  } | null>(null);

  // Capture/relâche tolérante (méthodes absentes sous happy-dom → ignorées).
  const capture = (e: React.PointerEvent<HTMLImageElement>): void => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* non supporté */
    }
  };
  const release = (e: React.PointerEvent<HTMLImageElement>): void => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* déjà relâché */
    }
  };

  // Panoramique par glisser à UN doigt (l'image doit déborder — `pannable`).
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const beginDrag = (e: React.PointerEvent<HTMLImageElement>): void => {
    if (e.button !== 0 || !live.current.pannable) return;
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: live.current.offset.x,
      oy: live.current.offset.y,
    };
    setDragging(true);
  };
  const moveDrag = (e: React.PointerEvent<HTMLImageElement>): void => {
    const d = dragRef.current;
    if (!d) return;
    const { framed: nat, size: sz, scale: cur } = live.current;
    setInteracted(true);
    setOffset({
      x: clampOffset(d.ox + (e.clientX - d.x), nat.w * cur, sz.w),
      y: clampOffset(d.oy + (e.clientY - d.y), nat.h * cur, sz.h),
    });
  };
  const endDrag = (): void => {
    dragRef.current = null;
    setDragging(false);
  };

  // Centre du conteneur en coordonnées écran (repère des milieux de doigts).
  const containerCenter = (): { cx: number; cy: number } => {
    const el = containerRef.current;
    if (!el) return { cx: 0, cy: 0 };
    const r = el.getBoundingClientRect();
    return { cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  };
  // Deux premiers pointeurs actifs (ou `null` s'il n'y en a pas deux).
  const twoPointers = (): [{ x: number; y: number }, { x: number; y: number }] | null => {
    const pts = [...pointersRef.current.values()];
    return pts.length >= 2 ? [pts[0]!, pts[1]!] : null;
  };
  const startPinch = (): void => {
    const two = twoPointers();
    if (!two) return;
    const [a, b] = two;
    const { cx, cy } = containerCenter();
    pinchRef.current = {
      dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      scale: live.current.scale,
      offset: { ...live.current.offset },
      midX: (a.x + b.x) / 2 - cx,
      midY: (a.y + b.y) / 2 - cy,
    };
  };
  const applyPinch = (): void => {
    const pin = pinchRef.current;
    const two = twoPointers();
    const { bounds: b, framed: nat, size: sz } = live.current;
    if (!pin || !two || !b) return;
    const [a, c] = two;
    const dist = Math.hypot(a.x - c.x, a.y - c.y);
    if (dist <= 0) return;
    const { cx, cy } = containerCenter();
    const midX = (a.x + c.x) / 2 - cx;
    const midY = (a.y + c.y) / 2 - cy;
    const ns = clamp((pin.scale * dist) / pin.dist, b.min, b.max);
    const k = ns / pin.scale;
    setInteracted(true);
    setScale(ns);
    setOffset({
      x: clampOffset(pinchOffset(pin.offset.x, pin.midX, midX, k), nat.w * ns, sz.w),
      y: clampOffset(pinchOffset(pin.offset.y, pin.midY, midY, k), nat.h * ns, sz.h),
    });
  };

  // Balayage tactile préc./suiv. — actif UNIQUEMENT hors zoom (`!pannable`) : au
  // zoom, le glisser panoramique prime. Composé avec le pan/pincer ci-dessous.
  const swipe = useSwipeNav({
    enabled: !pannable,
    canPrev,
    canNext,
    onPrev: onPrev ?? (() => {}),
    onNext: onNext ?? (() => {}),
  });

  const onImagePointerDown = (e: React.PointerEvent<HTMLImageElement>): void => {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    capture(e);
    if (pointersRef.current.size >= 2) {
      // 2e doigt → PINCER : annule le swipe et le pan 1 doigt éventuels, fige l'instantané.
      swipe.onPointerCancel();
      endDrag();
      startPinch();
      return;
    }
    swipe.onPointerDown(e);
    beginDrag(e);
  };
  const onImagePointerMove = (e: React.PointerEvent<HTMLImageElement>): void => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pinchRef.current && pointersRef.current.size >= 2) {
      applyPinch();
      return;
    }
    moveDrag(e);
  };
  const onImagePointerUp = (e: React.PointerEvent<HTMLImageElement>): void => {
    const wasPinch = pinchRef.current !== null;
    pointersRef.current.delete(e.pointerId);
    release(e);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    // Après un pincer, le lever d'un doigt ne doit PAS être interprété comme un
    // swipe/pan (le swipe a déjà été annulé à l'entrée du pincer).
    if (wasPinch) {
      swipe.onPointerCancel();
      endDrag();
      return;
    }
    swipe.onPointerUp(e);
    endDrag();
  };
  const onImagePointerCancel = (e: React.PointerEvent<HTMLImageElement>): void => {
    pointersRef.current.delete(e.pointerId);
    release(e);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    swipe.onPointerCancel();
    endDrag();
  };

  const onDoubleClick = (e: React.MouseEvent<HTMLImageElement>): void => {
    const b = live.current.bounds;
    const el = containerRef.current;
    if (!b || !el) return;
    const rect = el.getBoundingClientRect();
    const cx = e.clientX - (rect.left + rect.width / 2);
    const cy = e.clientY - (rect.top + rect.height / 2);
    if (Math.abs(live.current.scale - b.initial) < 1e-3) {
      applyZoom(clamp(Math.max(b.initial * 2, 1), b.min, b.max), cx, cy);
    } else {
      reset();
    }
  };

  /*
   * Retour galerie au clic sur le FOND (la marge autour de l'image).
   *
   * La règle « geste ENTIER sur le fond » est déléguée à `useScrimDismiss` — c'est la même
   * que celle des modales, et pour la même raison : un `click` naît sur l'ANCÊTRE COMMUN
   * du `pointerdown` et du `pointerup`, donc un geste commencé SUR L'IMAGE et relâché dans
   * la marge visait le conteneur. Ne restait ici qu'un seuil de distance, que le fond ajoute
   * à la garde commune : un glisser franc d'un bout à l'autre de la marge n'est pas un clic.
   */
  const backdropDownRef = useRef<{ x: number; y: number } | null>(null);
  const backdrop = useScrimDismiss(
    useCallback(
      (e: React.MouseEvent<Element>): void => {
        const down = backdropDownRef.current;
        backdropDownRef.current = null;
        if (!onBackdropClick || !down) return;
        const moved = Math.abs(e.clientX - down.x) + Math.abs(e.clientY - down.y);
        if (moved > CLICK_DRAG_THRESHOLD) return;
        onBackdropClick();
      },
      [onBackdropClick],
    ),
  );
  const onContainerPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    backdrop.onPointerDown(e);
    backdropDownRef.current = { x: e.clientX, y: e.clientY };
  };

  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>): void => {
    setLoaded(true);
    if (natural.w <= 0 || natural.h <= 0) {
      const img = e.currentTarget;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      }
    }
  };

  return (
    /* Conteneur de zoom/panoramique : les gestes pointeur (pincer, glisser, clic sur
       le fond) DOUBLENT des commandes déjà accessibles au clavier (zoom clavier
       présent, `Échap`, bouton « Fermer »). Ce n'est donc pas un contrôle à exposer,
       mais une surface gestuelle — lui donner un rôle interactif serait trompeur. */
    <div
      ref={containerRef}
      {...backdrop}
      onPointerDown={onContainerPointerDown}
      // `touch-none` : le pincer à deux doigts pilote le zoom via JS — on empêche
      // le navigateur de zoomer la PAGE (ou de scruter un double-tap) sur toute la
      // frame, y compris les marges autour de l'image.
      className="relative flex h-full w-full touch-none items-center justify-center overflow-hidden"
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        // Contenu activement regardé (original plein écran) → priorité HAUTE : prime sur
        // d'éventuelles vignettes « low » encore en vol.
        fetchPriority="high"
        onLoad={onLoad}
        onError={onError}
        onPointerDown={onImagePointerDown}
        onPointerMove={onImagePointerMove}
        onPointerUp={onImagePointerUp}
        onPointerCancel={onImagePointerCancel}
        onDoubleClick={onDoubleClick}
        style={
          ready
            ? {
                // Boîte CSS = dimensions NON pivotées (c'est `rotate()` ci-dessous qui
                // la fait tourner) ; `dispW`/`dispH`, eux, décrivent l'emprise APRÈS
                // rotation et ne servent qu'au bornage.
                width: `${natural.w * effScale}px`,
                height: `${natural.h * effScale}px`,
                maxWidth: 'none',
                // Composition lue de gauche à droite : la rotation s'applique d'abord
                // (autour du centre, origine par défaut), la translation de panoramique
                // ensuite — donc dans les axes de l'ÉCRAN, comme le geste de l'utilisateur.
                transform:
                  rotationDeg === 0
                    ? `translate(${clampedX}px, ${clampedY}px)`
                    : `translate(${clampedX}px, ${clampedY}px) rotate(${rotationDeg}deg)`,
                cursor: pannable ? (dragging ? 'grabbing' : 'grab') : 'default',
                touchAction: 'none',
              }
            : undefined
        }
        className={ready ? 'select-none' : 'max-h-full max-w-full select-none object-contain'}
      />

      {!loaded && (
        <div
          role="status"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span className="rounded-md bg-black/50 px-3 py-1 text-sm text-white/80">
            {t('viewer.loading')}
          </span>
        </div>
      )}

      {ready && (
        <div className="absolute bottom-2 start-2 z-10 flex items-center gap-0.5 rounded-md bg-black/40 p-0.5 backdrop-blur">
          <button
            type="button"
            onClick={zoomOutCenter}
            disabled={effScale <= bounds.min + 1e-3}
            title={t('viewer.zoomOut')}
            aria-label={t('viewer.zoomOut')}
            className="rounded p-1.5 hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-30"
          >
            <MagnifyingGlassMinusIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={reset}
            title={t('viewer.zoomReset')}
            aria-label={`${interpolate(t('viewer.zoomLevel'), { percent })} — ${t('viewer.zoomReset')}`}
            className="min-w-14 rounded px-1 py-1.5 text-center text-xs tabular-nums hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {percent}%
          </button>
          <button
            type="button"
            onClick={zoomInCenter}
            disabled={effScale >= bounds.max - 1e-3}
            title={t('viewer.zoomIn')}
            aria-label={t('viewer.zoomIn')}
            className="rounded p-1.5 hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-30"
          >
            <MagnifyingGlassPlusIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
